import React, { useRef, useState } from 'react';
import { ChevronLeft, Camera, ImageIcon, Search, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';
import ImageUploader from '../ui/ImageUploader';
import { useRecordForm } from '../../hooks/useRecordForm';
import { useToast } from '../../hooks/useToast';

/**
 * 新增上课记录表单
 */
const RecordFormCreate = ({ onBack, onSave, isSaving = false }) => {
  const toast = useToast();
  
  const {
    students,
    courses,
    allCourses,
    selectedStudentId,
    selectedCourseId,
    remainingHours,
    giftBalance,
    loading,
    selectedDate,
    setSelectedDate,
    getTodayDate,
    handleStudentChange,
    handleCourseChange,
  } = useRecordForm();

  // 学生搜索下拉状态
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [studentSearchText, setStudentSearchText] = useState('');

  const displayedStudents = studentSearchText
    ? (students || []).filter(s => s.name.toLowerCase().includes(studentSearchText.toLowerCase()))
    : (students || []);

  const getSelectedStudentName = () => {
    if (!selectedStudentId) return '请选择学生';
    const found = (students || []).find(s => String(s.id) === String(selectedStudentId));
    return found ? found.name : '未知学生';
  };

  // 课程搜索下拉状态
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [courseSearchText, setCourseSearchText] = useState('');

  // 决定下拉框展示的课程列表
  const getDisplayedCourses = () => {
    if (courseSearchText) {
      // 有搜索词时，在所有课程中搜索
      return (allCourses || []).filter(c => c.name.toLowerCase().includes(courseSearchText.toLowerCase()));
    }
    // 没有搜索词时
    if (courses && courses.length > 0) {
      // 如果当前学员有带剩余课时的课程，默认展示这些
      return courses;
    }
    // 如果学员没有剩余课时的课程，直接展示所有可用课程
    return allCourses || [];
  };
  
  const displayedCourses = getDisplayedCourses();

  // 获取当前选中课程的展示名称
  const getSelectedCourseName = () => {
    if (!selectedStudentId) return '请先选择学生';
    if (loading) return '加载中...';
    if (!selectedCourseId) return '请选择课程';

    // 优先从 allCourses 找
    const foundInAll = (allCourses || []).find(c => String(c.id) === String(selectedCourseId));
    if (foundInAll) return foundInAll.name;

    // 如果 allCourses 中没找到（可能因为权限问题或异步未加载完），尝试从当前学员的 courses 中找
    const foundInStudent = (courses || []).find(c => String(c.id) === String(selectedCourseId));
    if (foundInStudent) return foundInStudent.name;

    return '未知课程';
  };

  // 图片上传状态
  const [sessionArtworkPhotos, setSessionArtworkPhotos] = useState([]);
  const [classroomPhotos, setClassroomPhotos] = useState([]);
  
  // 赠课抵扣状态
  const [useGiftHours, setUseGiftHours] = useState(false);

  // 表单字段 refs
  const dateRef = useRef(null);
  const studentIdRef = useRef(null);
  const courseIdRef = useRef(null);
  const consumedHoursRef = useRef(null);
  const remarksRef = useRef(null);

  // 处理保存
  const handleSave = () => {
    // 注意：防重入锁由父组件 App.jsx 的 isSavingRef 统一管理，此处不重复判断
    // 收集表单数据
    const formData = {
      date: dateRef.current?.value || '',
      student_id: parseInt(studentIdRef.current?.value) || null,
      course_id: parseInt(courseIdRef.current?.value) || null,
      teacher: '夏天老师',
      consumed_hours: parseFloat(consumedHoursRef.current?.value) || 0,
      remarks: remarksRef.current?.value || '',
      session_artwork_photos_urls: sessionArtworkPhotos,  // 作品图片
      classroom_photos_urls: classroomPhotos,             // 课堂照片
      use_reward_units: useGiftHours,                     // 是否抵扣赠课
    };

    // 验证必填字段
    if (!formData.date) {
      toast.error('请选择上课日期');
      return;
    }
    if (!formData.student_id) {
      toast.error('请选择学生');
      return;
    }
    if (!formData.course_id) {
      toast.error('请选择课程');
      return;
    }
    if (!formData.consumed_hours || formData.consumed_hours <= 0) {
      toast.error('请输入消耗课时');
      return;
    }

    // 检查剩余课时是否足够
    const availableHoursToUse = useGiftHours ? giftBalance : remainingHours;
    if (formData.consumed_hours > availableHoursToUse) {
      toast.error(`消耗课时不能超过${useGiftHours ? '可用' : '剩余课时'} ${availableHoursToUse}`);
      return;
    }

    // 赠送课时允许带一位小数
    if (useGiftHours && !/^\d+(\.\d)?$/.test(formData.consumed_hours.toString())) {
      toast.error('赠送课时核销最多支持一位小数');
      return;
    }

    console.log('[RecordFormCreate] 保存数据:', formData);
    onSave(formData);
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">新增上课记录</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {/* 基本信息 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7]">
          {/* 上课日期 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              上课日期
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={dateRef}
              type="date" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              value={selectedDate}
              onChange={(e) => {
                const newDate = e.target.value;
                setSelectedDate(newDate);
              }}
              required
            />
          </div>
          
          {/* 学生姓名 */}
          <div className="flex flex-col px-4 py-3 border-b border-[#EDF2F7] relative">
            <div className="flex items-center">
              <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
                学生姓名
                <span className="text-red-500">*</span>
              </label>
              <div className="flex-1 flex items-center justify-end gap-2 relative">
                {giftBalance > 0 && (
                  <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded-md font-bold border border-green-200 animate-pulse">
                    赠送
                  </span>
                )}
                
                <input type="hidden" ref={studentIdRef} value={selectedStudentId} />
                
                <div 
                  className="flex items-center justify-end flex-1 text-sm text-[#2D3748] cursor-pointer"
                  onClick={() => {
                    setShowStudentDropdown(!showStudentDropdown);
                    setStudentSearchText('');
                  }}
                >
                  <span className="truncate">{getSelectedStudentName()}</span>
                  <ChevronDown size={16} className="ml-1 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            </div>

            {showStudentDropdown && (
              <div className="absolute top-full right-4 left-24 mt-1 bg-white border border-[#EDF2F7] rounded-lg shadow-lg z-50 overflow-hidden flex flex-col max-h-60">
                <div className="p-2 border-b border-[#EDF2F7] bg-gray-50 flex items-center">
                  <Search size={14} className="text-gray-400 mr-2 flex-shrink-0" />
                  <input 
                    type="text" 
                    className="w-full bg-transparent text-sm outline-none text-[#2D3748]"
                    placeholder="搜索学生..."
                    value={studentSearchText}
                    onChange={(e) => setStudentSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="overflow-y-auto">
                  {displayedStudents.length > 0 ? (
                    displayedStudents.map(s => (
                      <div 
                        key={s.id} 
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex justify-between items-center ${String(selectedStudentId) === String(s.id) ? 'text-[#3182CE] bg-blue-50/50' : 'text-[#4A5568]'}`}
                        onClick={() => {
                          handleStudentChange(s.id);
                          setShowStudentDropdown(false);
                        }}
                      >
                        <span>{s.name}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">未找到相关学生</div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* 课程名称 */}
          <div className="flex flex-col px-4 py-3 border-b border-[#EDF2F7] relative">
            <div className="flex items-center">
              <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
                课程名称
                <span className="text-red-500">*</span>
              </label>
              
              <div className="flex-1 relative">
                 <input type="hidden" ref={courseIdRef} value={selectedCourseId} />
                 
                 <div 
                   className={`flex items-center justify-between w-full text-sm ${!selectedStudentId || loading ? 'text-gray-400' : 'text-[#2D3748]'} cursor-pointer`}
                   onClick={() => {
                     if (selectedStudentId && !loading) {
                       setShowCourseDropdown(!showCourseDropdown);
                       setCourseSearchText('');
                     }
                   }}
                 >
                   <span className="flex-1 text-right truncate">
                     {getSelectedCourseName()}
                   </span>
                   <ChevronDown size={16} className="ml-1 text-gray-400 flex-shrink-0" />
                 </div>
              </div>
            </div>
            
            {showCourseDropdown && (
              <div className="absolute top-full right-4 left-24 mt-1 bg-white border border-[#EDF2F7] rounded-lg shadow-lg z-50 overflow-hidden flex flex-col max-h-60">
                 <div className="p-2 border-b border-[#EDF2F7] bg-gray-50 flex items-center">
                   <Search size={14} className="text-gray-400 mr-2 flex-shrink-0" />
                   <input 
                     type="text" 
                     className="w-full bg-transparent text-sm outline-none text-[#2D3748]"
                     placeholder="搜索可用课程..."
                     value={courseSearchText}
                     onChange={(e) => setCourseSearchText(e.target.value)}
                     onClick={(e) => e.stopPropagation()}
                   />
                 </div>
                 <div className="overflow-y-auto">
                   {displayedCourses.length > 0 ? (
                     displayedCourses.map(c => (
                       <div 
                         key={c.id} 
                         className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex justify-between items-center ${String(selectedCourseId) === String(c.id) ? 'text-[#3182CE] bg-blue-50/50' : 'text-[#4A5568]'}`}
                         onClick={() => {
                           handleCourseChange(c.id);
                           setShowCourseDropdown(false);
                         }}
                       >
                         <span>{c.name}</span>
                       </div>
                     ))
                   ) : (
                     <div className="px-3 py-4 text-center text-sm text-gray-400">未找到相关课程</div>
                   )}
                 </div>
              </div>
            )}
            
            {/* 点击外部关闭下拉框的透明遮罩 */}
            {showCourseDropdown && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowCourseDropdown(false)}
              />
            )}
          </div>
          
          {/* 消耗课时 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              消耗课时
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={consumedHoursRef}
              type="number" 
              placeholder="1" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              defaultValue="1"
              min="0"
              step={useGiftHours ? "0.1" : "0.1"}
              required
            />
          </div>
          
          {/* 剩余课时（只读） */}
          <div className="flex items-center px-4 py-3">
            <label className="w-24 text-sm font-medium text-[#4A5568]">
              剩余课时
            </label>
            <div className="flex-1 text-sm text-right text-[#3182CE] font-bold">
              {loading ? '计算中...' : (useGiftHours ? giftBalance : remainingHours)} 节
            </div>
          </div>

          {/* 赠送课时状态 (仅有余额 > 0 时显示) */}
          {giftBalance > 0 && (
            <div className="flex flex-col border-t border-[#EDF2F7] bg-blue-50/50">
              <div className="flex items-center px-4 py-3">
                <label className="w-24 text-sm font-medium text-[#3182CE] flex items-center gap-1">
                  赠送课时
                </label>
                <div className="flex-1 flex items-center justify-end gap-2">
                  <span className="text-sm text-[#3182CE] font-bold mr-2">
                    {giftBalance} 节
                  </span>
                  <button 
                    type="button"
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-[#3182CE] focus:ring-offset-2 ${useGiftHours ? 'bg-[#3182CE]' : 'bg-gray-300'}`}
                    onClick={() => setUseGiftHours(!useGiftHours)}
                  >
                    <span className="sr-only">使用赠送课时扣减</span>
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${useGiftHours ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
              {useGiftHours && (
                <div className="px-4 pb-2 text-[11px] text-[#3182CE]/70 text-right italic">
                  * 赠送课时核销支持一位小数扣减
                </div>
              )}
            </div>
          )}
        </div>

        {/* 作品图片上传 */}
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
              <ImageIcon size={16} className="text-[#3182CE]" />
              作品图片
            </label>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            <ImageUploader
              value={sessionArtworkPhotos}
              onChange={setSessionArtworkPhotos}
              maxFiles={5}
              uploadMeta={{
                imageType: '作品',
                studentId: selectedStudentId,
                studentName: students.find(s => s.id === parseInt(selectedStudentId))?.name,
                courseId: selectedCourseId,
                courseName: courses.find(c => c.id === parseInt(selectedCourseId))?.name,
                teacherName: '夏天老师'
              }}
              disabled={!selectedStudentId || !selectedCourseId}
              onSuccess={(urls) => console.log('作品上传成功:', urls)}
              onError={(error) => toast.error(`作品上传失败: ${error}`)}
            />
          </div>
        </div>

        {/* 课堂照片上传 */}
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
              <Camera size={16} className="text-[#3182CE]" />
              课堂照片
            </label>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            <ImageUploader
              value={classroomPhotos}
              onChange={setClassroomPhotos}
              maxFiles={10}
              uploadMeta={{
                imageType: '课堂',
                studentId: selectedStudentId,
                studentName: students.find(s => s.id === parseInt(selectedStudentId))?.name,
                courseId: selectedCourseId,
                courseName: courses.find(c => c.id === parseInt(selectedCourseId))?.name,
                teacherName: '夏天老师'
              }}
              disabled={!selectedStudentId || !selectedCourseId}
              onSuccess={(urls) => console.log('课堂照片上传成功:', urls)}
              onError={(error) => toast.error(`课堂照片上传失败: ${error}`)}
            />
          </div>
        </div>

        {/* 作品评价 */}
        <div>
          <label className="text-sm font-medium text-[#4A5568] mb-2 block px-1">作品评价</label>
          <textarea 
            ref={remarksRef}
            className="w-full bg-white border border-[#EDF2F7] rounded-xl p-3 text-sm h-32 focus:outline-none focus:border-[#3182CE]" 
            placeholder="请输入对学生本次表现的评价..."
          />
        </div>
      </main>

      {/* 底部按钮区 - 新增模式 */}
      <div className="h-[80px] bg-white border-t border-[#EDF2F7] px-6 flex items-center justify-between flex-shrink-0 z-10">
        <Button variant="secondary" onClick={onBack} className="flex-1 mr-4" disabled={isSaving}>取消</Button>
        <Button variant="primary" className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving ? '保存中...' : '保存'}
        </Button>
      </div>
    </div>
  );
};

export default RecordFormCreate;
